'use client';

import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../utils/cn";
import { Flag, type FlagRegion } from "../Flag/Flag";

/**
 * Chip — MV5 ComponentSet mirror.
 *
 * Variants:
 *   region          — Region tag with a leading flag SVG.
 *   vertical        — Vertical tag (AI Eng / AI Marketing / Vibe Coding).
 *   founding-member — Special violet-filled chip for the founding cohort.
 *   status          — Semantic status pill (uses `tone` prop).
 *
 * The `region` variant requires the `region` prop; other variants ignore it.
 * The `status` variant uses `tone` to select semantic color; other variants
 * ignore it.
 */
const chipVariants = cva(
  [
    "inline-flex items-center gap-space-4",
    "font-sans font-medium whitespace-nowrap",
    "rounded-chip border",
    "transition-colors duration-micro ease-standard",
  ].join(" "),
  {
    variants: {
      variant: {
        region:
          "bg-chrome-panel text-ink-primary border-chrome-border pl-space-4 pr-space-8 py-space-4 text-caption-12",
        vertical:
          "bg-chrome-panel text-ink-primary border-chrome-border px-space-8 py-space-4 text-caption-12 uppercase tracking-[0.06em]",
        "founding-member":
          "bg-accent-violet text-white border-accent-violet px-space-8 py-space-4 text-caption-12",
        status:
          "px-space-8 py-space-4 text-caption-12",
      },
    },
    defaultVariants: {
      variant: "vertical",
    },
  },
);

const statusToneClass: Record<ChipTone, string> = {
  success:
    "bg-[color:var(--status-success)]/10 text-status-success border-[color:var(--status-success)]/30",
  warn:
    "bg-[color:var(--status-warn)]/10 text-status-warn border-[color:var(--status-warn)]/30",
  error:
    "bg-[color:var(--status-error)]/10 text-status-error border-[color:var(--status-error)]/30",
  neutral:
    "bg-chrome-panel text-ink-muted border-chrome-border",
};

export type ChipTone = "success" | "warn" | "error" | "neutral";

export interface ChipProps
  extends Omit<React.HTMLAttributes<HTMLSpanElement>, "children">,
    VariantProps<typeof chipVariants> {
  /** Chip label content. */
  children: React.ReactNode;
  /** Region code — required when variant="region". */
  region?: FlagRegion;
  /** Semantic tone — used when variant="status". */
  tone?: ChipTone;
}

export const Chip = React.forwardRef<HTMLSpanElement, ChipProps>(function Chip(
  { className, variant = "vertical", region, tone = "neutral", children, ...rest },
  ref,
) {
  const isStatus = variant === "status";
  return (
    <span
      ref={ref}
      data-slot="chip"
      className={cn(
        chipVariants({ variant }),
        isStatus && statusToneClass[tone],
        className,
      )}
      {...rest}
    >
      {variant === "region" && region && (
        <Flag region={region} size={16} aria-hidden />
      )}
      {children}
    </span>
  );
});

Chip.displayName = "Chip";

export { chipVariants };
